import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import {
  CreateFinancialInstitutionFailure,
  CreateFinancialInstitutionRequest,
  CreateFinancialInstitutionSuccess,
  DeleteFinancialInstitutionFailure,
  DeleteFinancialInstitutionRequest,
  DeleteFinancialInstitutionSuccess,
  FetchFinancialInstitutionFailure,
  FetchFinancialInstitutionRequest,
  FetchFinancialInstitutionSuccess,
  FetchFinancialInstitutionsFailure,
  FetchFinancialInstitutionsRequest,
  FetchFinancialInstitutionsSuccess,
  UpdateFinancialInstitutionFailure,
  UpdateFinancialInstitutionRequest,
  UpdateFinancialInstitutionSuccess,
} from './financial-institutions.actions'
import { FinancialInstitutionsService } from './financial-institutions.service'
import * as FinancialInstitutions from './financial-institutions.types'

@Injectable()
export class FinancialInstitutionsEffects {
  constructor(
    private actions: Actions,
    private FinancialInstitutionsService: FinancialInstitutionsService,
    private router: Router,
  ) {}

  FetchFinancialInstitutions$ = createEffect(() =>
    this.actions.pipe(
      ofType(FinancialInstitutions.FETCH_FINANCIAL_INSTITUTIONS_REQUEST),
      switchMap((action: FetchFinancialInstitutionsRequest) => {
        return this.FinancialInstitutionsService.fetchFinancialInstitutions(
          action.payload,
        ).pipe(
          map((result) => {
            return new FetchFinancialInstitutionsSuccess(
              result.data.results,
              result.data.totalCount,
              result.data.filteredCount,
            )
          }),
          catchError((err, caught) => {
            return of(new FetchFinancialInstitutionsFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchFinancialInstitution$ = createEffect(() =>
    this.actions.pipe(
      ofType(FinancialInstitutions.FETCH_FINANCIAL_INSTITUTION_REQUEST),
      switchMap((action: FetchFinancialInstitutionRequest) => {
        return this.FinancialInstitutionsService.fetchFinancialInstitution(
          action.id,
        ).pipe(
          map((result) => {
            return new FetchFinancialInstitutionSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchFinancialInstitutionFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  DeleteFinancialInstitution$ = createEffect(() =>
    this.actions.pipe(
      ofType(FinancialInstitutions.DELETE_FINANCIAL_INSTITUTION_REQUEST),
      switchMap((action: DeleteFinancialInstitutionRequest) => {
        return this.FinancialInstitutionsService.deleteFinancialInstitution(
          action.id,
        ).pipe(
          switchMap(() => {
            return [new DeleteFinancialInstitutionSuccess()]
          }),
          catchError((err, caught) => {
            return of(new DeleteFinancialInstitutionFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  CreateFinancialInstitution$ = createEffect(() =>
    this.actions.pipe(
      ofType(FinancialInstitutions.CREATE_FINANCIAL_INSTITUTION_REQUEST),
      switchMap((action: CreateFinancialInstitutionRequest) => {
        return this.FinancialInstitutionsService.createFinancialInstitution(
          action.payload,
        ).pipe(
          switchMap(() => {
            this.router.navigate(['/admin/financial-institutions'])
            return [new CreateFinancialInstitutionSuccess()]
          }),
          catchError(() => {
            return of(new CreateFinancialInstitutionFailure({}))
          }),
        )
      }),
    ),
  )

  UpdateFinancialInstitution$ = createEffect(() =>
    this.actions.pipe(
      ofType(FinancialInstitutions.UPDATE_FINANCIAL_INSTITUTION_REQUEST),
      switchMap((action: UpdateFinancialInstitutionRequest) => {
        return this.FinancialInstitutionsService.updateFinancialInstitution(
          action.id,
          action.payload,
        ).pipe(
          switchMap(() => {
            this.router.navigate(['/admin/financial-institutions'])
            return [new UpdateFinancialInstitutionSuccess()]
          }),
          catchError(() => {
            return of(new UpdateFinancialInstitutionFailure({}))
          }),
        )
      }),
    ),
  )
}
